import React from 'react';

/**
 * StatusPill (Aurora Cockpit) — the verdict / status pill of the round-2
 * design system (`.da-status`; see src/styles/auroraCockpit.css). A glow-dot
 * + uppercase mono label, tinted by a semantic token.
 *
 * NOTE — coexistence: this is DISTINCT from the legacy Overview-UX
 * `src/components/ui/status-pill.tsx` (which uses Tailwind bg-/text- color
 * variants). That one stays in place for current Overview callers; this
 * Aurora primitive is what the round-2 surfaces consume. They live in
 * separate paths so a case-insensitive filesystem can hold both.
 *
 * Variants map to the cockpit verdict semantics:
 *   go        → GO            (--success)
 *   cond      → Conditional   (--warning)
 *   cond-high → Conditional·High (--conditional-high)
 *   nogo      → Blocked       (--danger)
 *   info      → In review     (--primary)
 *   neutral   → Pending       (muted, no glow)
 */
export type StatusPillVariant = 'go' | 'cond' | 'cond-high' | 'nogo' | 'info' | 'neutral';

const VARIANT_MODIFIER: Record<StatusPillVariant, string> = {
  go: 'da-status--go',
  cond: 'da-status--cond',
  'cond-high': 'da-status--condh',
  nogo: 'da-status--nogo',
  info: 'da-status--info',
  neutral: 'da-status--neutral',
};

export interface StatusPillProps {
  variant?: StatusPillVariant;
  children: React.ReactNode;
  /** Hide the leading glow dot (rare — e.g. when the label is itself the cue). */
  hideDot?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  variant = 'neutral',
  children,
  hideDot = false,
  className = '',
  'data-testid': testId,
}) => (
  <span
    className={`da-status ${VARIANT_MODIFIER[variant]} ${className}`.trim()}
    data-testid={testId}
  >
    {hideDot ? null : <span className="da-status__dot" aria-hidden />}
    {children}
  </span>
);
